import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";

/** `Intl.NumberFormat` separates the number from its unit with U+00A0 in many locales. */
export const stripNbsp = (value: string): string => value.replace(/\u00A0/g, "");

/**
 * Keeps the gap only for an ISO code ("1.234 CHF"), where letters running onto digits read as a
 * single token; a glyph gets none ("1.234€").
 */
export const stripCurrencyNbsp = (value: string, locale: string, currency: CurrencyCode): string =>
  currencyPattern(locale, currency).spaced ? value : stripNbsp(value);

/**
 * Amounts are formatted through here so no caller can produce a currency string that skips the
 * locale's spacing rule.
 */
export const formatCurrencyValue = (
  value: number,
  locale: string,
  currency: CurrencyCode,
  options: Intl.NumberFormatOptions,
): string =>
  stripCurrencyNbsp(
    new Intl.NumberFormat(locale, { ...options, style: "currency", currency }).format(value),
    locale,
    currency,
  );

/**
 * How `locale` writes amounts in `currency`: which symbol, whether it leads the number
 * (en-US "$1,234") or trails it (de-DE "1.234 €"), and whether it needs a gap.
 *
 * Shortened magnitudes ("1.2M") are composed by hand and so cannot come out of `Intl` whole, but
 * they still have to place the symbol the way the locale does.
 *
 * `spaced` marks the currencies `Intl` has no glyph for, where it prints the ISO code instead.
 */
export const currencyPattern = (
  locale: string,
  currency: CurrencyCode,
): { symbol: string; symbolFirst: boolean; spaced: boolean } => {
  const parts = new Intl.NumberFormat(locale, { style: "currency", currency }).formatToParts(1);
  const symbolIndex = parts.findIndex((part) => part.type === "currency");
  const numberIndex = parts.findIndex((part) => part.type === "integer");
  const symbol = parts[symbolIndex]?.value ?? currency;

  return {
    symbol,
    symbolFirst: symbolIndex < numberIndex,
    spaced: symbol === currency,
  };
};
